"use client";

import { useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { DisclosureChevron } from "@/components/optilase/icons";
import { accentOn, headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";
import { headingLines } from "@/lib/heading-lines";

export type IconPill = {
  icon?: string;
  iconAlt?: string;
  label: string;
  body?: string;
};

export type IconPillsViewProps = {
  heading: string;
  image?: string;
  imageAlt?: string;
  imageFocus?: string;
  intro?: string;
  listHeading?: string;
  pills: IconPill[];
  headingTone?: "white" | "teal";
  surface?: Surface | null;
};

export default function IconPillsView({
  heading,
  image,
  imageAlt = "",
  imageFocus,
  intro,
  listHeading,
  pills,
  headingTone = "white",
  surface,
}: IconPillsViewProps) {
  const detailed = pills.some((p) => p.body);
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(surface ? "" : "bg-teal-08")}
    >
      {image ? (
        <div className="relative h-[240px] w-full overflow-hidden md:h-80 lg:h-[400px]">
          <AssetImage
            src={image}
            focus={imageFocus}
            alt={imageAlt}
            fill
            sizes="100vw"
            className="object-cover object-top"
          />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-2/3"
            style={{
              backgroundImage: `linear-gradient(to top, ${
                surface?.backgroundColor ?? "#00645e"
              }, transparent)`,
            }}
          />
        </div>
      ) : null}
      <div
        className={cn(
          "px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20",
          image && "pt-0 md:pt-0 lg:pt-0",
        )}
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-8 lg:gap-12">
          <div className="flex flex-col gap-5">
            <h2
              className={cn(
                "text-section",
                headingOn(surface, headingTone === "teal" ? "text-teal-05" : "text-white"),
              )}
            >
              {headingLines(heading)}
            </h2>
            {intro ? (
              <p
                className={cn(
                  "max-w-[867px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl",
                  onSurface(surface, "text-white", "text-grey-07", "text-white"),
                )}
              >
                {intro}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-5 lg:gap-6">
            {listHeading ? (
              <p
                className={cn(
                  "text-h5 font-semibold tracking-[-0.01em]",
                  accentOn(surface, "text-teal-04"),
                )}
              >
                {listHeading}
              </p>
            ) : null}

            <ul className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-4 lg:gap-x-4 lg:gap-y-4">
              {pills.map((pill) => {
                const isOpen = open === pill.label;
                const icon = (size: string, imgSize: number, tone: string) => (
                  <span
                    className={cn(
                      "relative flex shrink-0 items-center justify-center rounded-full",
                      size,
                      tone,
                    )}
                  >
                    {pill.icon ? (
                      <AssetImage
                        src={pill.icon}
                        alt={pill.iconAlt || pill.label}
                        aria-hidden
                        width={imgSize}
                        height={imgSize}
                        className={cn("object-contain", imgSize === 28 ? "size-7" : "size-6")}
                      />
                    ) : null}
                  </span>
                );

                return (
                  <li key={pill.label} className="md:flex">
                    <div className="w-full md:hidden">
                      {detailed && pill.body ? (
                        <div
                          className={cn(
                            "flex flex-col gap-2 rounded-xl bg-white transition-[padding]",
                            isOpen ? "p-4" : "p-3",
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => setOpen(isOpen ? null : pill.label)}
                            aria-expanded={isOpen}
                            className="flex w-full cursor-pointer items-center gap-4 text-left"
                          >
                            <span className="bg-teal-05 flex size-10 shrink-0 items-center justify-center rounded-full">
                              {pill.icon ? (
                                <AssetImage
                                  src={pill.icon}
                                  alt={pill.iconAlt || pill.label}
                                  aria-hidden
                                  width={20}
                                  height={20}
                                  className="size-5 object-contain"
                                />
                              ) : null}
                            </span>
                            <span
                              className={cn(
                                "text-grey-08 min-w-0 flex-1 leading-[1.2] font-semibold tracking-[-0.01em]",
                                isOpen ? "text-lg" : "text-base",
                              )}
                            >
                              {pill.label}
                            </span>
                            <DisclosureChevron
                              className={cn(
                                "text-teal-07 shrink-0 transition-transform",
                                isOpen && "rotate-90",
                              )}
                            />
                          </button>
                          {isOpen ? (
                            <p className="text-grey-06 text-base leading-normal tracking-[-0.01em]">
                              {pill.body}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="flex items-center gap-4 rounded-xl bg-white p-3">
                          <span
                            className={cn(
                              "flex size-10 shrink-0 items-center justify-center rounded-full",
                              detailed ? "bg-teal-05" : "bg-teal-02",
                            )}
                          >
                            {pill.icon ? (
                              <AssetImage
                                src={pill.icon}
                                alt={pill.iconAlt || pill.label}
                                aria-hidden
                                width={20}
                                height={20}
                                className="size-5 object-contain"
                              />
                            ) : null}
                          </span>
                          <span className="text-grey-08 min-w-0 flex-1 text-base leading-[1.2] font-semibold tracking-[-0.01em]">
                            {pill.label}
                          </span>
                        </div>
                      )}
                    </div>

                    <div
                      className={cn(
                        "hidden w-full gap-4 rounded-2xl bg-white md:flex",
                        detailed ? "items-center py-4 pr-7 pl-5 lg:gap-6" : "items-center p-5",
                      )}
                    >
                      {icon(
                        detailed ? "size-14" : "size-12",
                        detailed ? 28 : 24,
                        detailed ? "bg-teal-05" : "bg-teal-02",
                      )}
                      {detailed ? (
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-grey-08 text-lg leading-[1.56] font-bold lg:text-xl">
                            {pill.label}
                          </span>
                          {pill.body ? (
                            <span className="text-grey-06 text-base leading-relaxed tracking-[-0.01em]">
                              {pill.body}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-grey-08 text-base leading-snug tracking-[-0.01em] lg:text-lg">
                          {pill.label}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
